import { useEffect, useMemo, useState } from 'react';
import { getDeposits } from '../../../../services/depositsService';
import { hydrateFilters } from '../../../../services/productService';
import { getSearchParams, handleSearchParams } from '../../../../helpers/handleSearchParams';
import { BASE_FILTERS, getCurrentCampaign } from './constants';

/**
 * Hook que concentra TODO el estado de los filtros del report de
 * Eficacia: temporada/año, depósitos, búsqueda por familia, género /
 * categoría / subcategoría. También hidrata las opciones (back) y
 * sincroniza la URL.
 *
 * Devuelve una API plana que `ViewReportEfficacy` consume y pasa a
 * `FiltersCard`, `ActiveFilterChips` y al fetcher de datos.
 */
export const useEfficacyFilters = () => {
  // Inicial: la campaña actual o lo que venga en URL (?season&year).
  const initialCampaign = useMemo(() => {
    const urlSeason = getSearchParams('season');
    const urlYear = getSearchParams('year');
    if ((urlSeason === 'VERANO' || urlSeason === 'INVIERNO') && urlYear)
      return { season: urlSeason, year: urlYear };
    return getCurrentCampaign();
  }, []);

  const [season, setSeason] = useState(initialCampaign.season);
  const [year, setYear] = useState(initialCampaign.year);

  // Alcance de depósitos en tres dimensiones acumulativas (unión), cada una
  // con su query param homónimo en la API:
  //   - depositsValue  → ids concretos, o el sentinel 'ALL' (incluir central).
  //   - depositRolesValue  → roles de depositType ('store:outlet'…).
  //   - depositGroupsValue → ids de grupos (el back los expande a hojas).
  // Todo vacío = default del back: perímetro de venta (tiendas, outlet y
  // canales remotos; fuera central, CEDIS, devoluciones y merma).
  const [depositsValue, setDepositsValue] = useState(() => getSearchParams('deposits') || '');
  const [depositRolesValue, setDepositRolesValue] = useState(() => getSearchParams('depositRoles') || '');
  const [depositGroupsValue, setDepositGroupsValue] = useState(() => getSearchParams('depositGroups') || '');
  const [depositOptions, setDepositOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);

  // Dims de producto. Cada slug guarda { name, slug, options, value }.
  const [filters, setFilters] = useState(BASE_FILTERS);

  // Búsqueda por familia: el TextField escribe en `familySearch` y un
  // debounce de 300ms copia a `familyQuery`, que es lo que va al back.
  const [familySearch, setFamilySearch] = useState(() => getSearchParams('family') || '');
  const [familyQuery, setFamilyQuery] = useState(familySearch);
  useEffect(() => {
    const t = setTimeout(() => setFamilyQuery(familySearch.trim()), 300);
    return () => clearTimeout(t);
  }, [familySearch]);

  // `ready` marca que ya están hidratados los catálogos (filtros + depósitos)
  // — el effect de fetch espera a esto para no disparar requests con
  // opciones vacías.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    Promise.all([
      hydrateFilters(
        Object.keys(BASE_FILTERS)
          .map((k) => BASE_FILTERS[k])
          .reduce((acc, f) => ({ ...acc, [f.slug]: f }), {}),
      ),
      getDeposits({ limit: 1000, excludeGroups: true }),
      getDeposits({ limit: 100, onlyGroups: true }),
    ]).then(([f, d, g]) => {
      setFilters((prev) => ({ ...prev, ...f }));
      setDepositOptions(d.data.data.map((dep) => ({ label: dep.name, value: dep.id })));
      setGroupOptions((g.data.data || []).map((grp) => ({ label: grp.name, value: grp.id })));
      setReady(true);
    });
  }, []);

  // Setter único para el selector de alcance: recibe la selección completa
  // del popover y la reparte a los tres estados serializados.
  const setDepositScope = ({ all, roles, groups, deposits }) => {
    setDepositsValue(all ? 'ALL' : deposits.join(':'));
    setDepositRolesValue(all ? '' : roles.join(':'));
    setDepositGroupsValue(all ? '' : groups.join(':'));
  };

  const handleFilter = ({ slug, value }) => {
    setFilters((prev) => ({ ...prev, [slug]: { ...prev[slug], value } }));
  };

  const clearFilters = () => {
    setFilters((prev) => {
      const next = { ...prev };
      Object.keys(BASE_FILTERS).forEach((k) => {
        next[k] = { ...next[k], value: null };
      });
      return next;
    });
    // El alcance de depósitos también es un filtro: vuelve al default
    // (perímetro de venta).
    setDepositsValue('');
    setDepositRolesValue('');
    setDepositGroupsValue('');
  };

  // Persistir TODO el estado relevante en la URL. Los componentes
  // que lean directamente la URL (deep-link, refresh) recuperan el
  // estado tal cual estaba.
  const persistInUrl = () => {
    handleSearchParams('season', season);
    handleSearchParams('year', year);
    handleSearchParams('deposits', depositsValue);
    handleSearchParams('depositRoles', depositRolesValue);
    handleSearchParams('depositGroups', depositGroupsValue);
    handleSearchParams('family', familyQuery);
    Object.keys(filters).forEach((k) => handleSearchParams(k, filters[k]?.value));
  };

  // Sólo los filtros con valor — se inyectan en los params de la API
  // (y de los exports). Memoizado para que el useEffect del fetch no
  // se dispare por cambios de identidad.
  const validFilters = useMemo(
    () =>
      Object.keys(filters)
        .map((k) => (filters[k]?.value ? { slug: k, value: filters[k].value } : null))
        .filter(Boolean)
        .reduce((acc, f) => ({ ...acc, [f.slug]: f.value }), {}),
    [filters],
  );

  return {
    season,
    setSeason,
    year,
    setYear,
    depositsValue,
    setDepositsValue,
    depositRolesValue,
    depositGroupsValue,
    setDepositScope,
    depositOptions,
    groupOptions,
    filters,
    handleFilter,
    clearFilters,
    familySearch,
    setFamilySearch,
    familyQuery,
    ready,
    validFilters,
    persistInUrl,
  };
};
